import { StatsData } from '../hooks/useStats';
import { REGIONS, CATEGORIES } from '../types';

interface StatsPageProps {
  stats: StatsData;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000_000) return (n / 1_000_000_000).toFixed(1) + 'B';
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
  if (n >= 1_000) return (n / 1_000).toFixed(1) + 'K';
  return n.toLocaleString();
}

function regionLabel(code: string): string {
  const r = REGIONS.find((x) => x.id === code);
  return r ? `${r.flag} ${r.name}` : code;
}

function categoryLabel(id: string): string {
  return CATEGORIES.find((c) => c.id === id)?.name || id;
}

function BarList({
  rows,
  labelFor,
}: {
  rows: { key: string; value: number }[];
  labelFor: (key: string) => string;
}) {
  const max = Math.max(1, ...rows.map((r) => r.value));
  return (
    <div className="stats-bars">
      {rows.map((r) => (
        <div className="stats-bar-row" key={r.key}>
          <span className="stats-bar-label">{labelFor(r.key)}</span>
          <div className="stats-bar-track">
            <div className="stats-bar-fill" style={{ width: `${(r.value / max) * 100}%` }} />
          </div>
          <span className="stats-bar-value">{formatNumber(r.value)}</span>
        </div>
      ))}
    </div>
  );
}

export function StatsPage({ stats }: StatsPageProps) {
  const { biggestMover, allTimeTop } = stats;

  return (
    <div className="stats-page">
      <div className="stats-numbers">
        <div className="stats-number-card">
          <div className="stats-number-value">{formatNumber(stats.videosTracked)}</div>
          <div className="stats-number-label">Videos tracked</div>
        </div>
        <div className="stats-number-card">
          <div className="stats-number-value">{formatNumber(stats.totalViewsTracked)}</div>
          <div className="stats-number-label">Views represented</div>
        </div>
        <div className="stats-number-card">
          <div className="stats-number-value">{formatNumber(stats.snapshotsRecorded)}</div>
          <div className="stats-number-label">Chart snapshots taken</div>
        </div>
        <div className="stats-number-card">
          <div className="stats-number-value">{stats.daysCollecting.toLocaleString()}</div>
          <div className="stats-number-label">Days of history</div>
        </div>
      </div>

      <div className="stats-columns">
        <section className="stats-section">
          <h2 className="stats-section-title">📈 Views gained this week, by category</h2>
          {stats.topCategories.length > 0 ? (
            <BarList
              rows={stats.topCategories.map((c) => ({ key: c.categoryId, value: c.deltaViews }))}
              labelFor={categoryLabel}
            />
          ) : (
            <p className="stats-empty">Still filling in — check back soon.</p>
          )}
        </section>

        <section className="stats-section">
          <h2 className="stats-section-title">🌍 Views gained this week, by country</h2>
          {stats.topRegions.length > 0 ? (
            <BarList
              rows={stats.topRegions.map((r) => ({ key: r.region, value: r.deltaViews }))}
              labelFor={regionLabel}
            />
          ) : (
            <p className="stats-empty">Still filling in — check back soon.</p>
          )}
        </section>
      </div>

      <div className="stats-columns">
        {biggestMover && (
          <section className="stats-section stats-callout">
            <h2 className="stats-section-title">🚀 Biggest mover this week</h2>
            <div className="stats-callout-body">
              {biggestMover.thumbnail_url && (
                <img src={biggestMover.thumbnail_url} alt="" className="stats-callout-thumb" />
              )}
              <div className="stats-callout-info">
                <div className="stats-callout-title">{biggestMover.title}</div>
                <div className="stats-callout-sub">{biggestMover.channel_title}</div>
                <div className="stats-callout-metric">
                  +{formatNumber(biggestMover.delta_views)} views
                  <span className="stats-callout-metric-sub">
                    {' '}
                    ({formatNumber(biggestMover.delta_per_hour)}/hr) · {regionLabel(biggestMover.region)}
                    {biggestMover.category_id !== 'ALL' ? ` · ${categoryLabel(biggestMover.category_id)}` : ''}
                  </span>
                </div>
              </div>
            </div>
          </section>
        )}

        {allTimeTop && (
          <section className="stats-section stats-callout">
            <h2 className="stats-section-title">🏆 All-time champion</h2>
            <div className="stats-callout-body">
              <div className="stats-callout-info">
                <div className="stats-callout-title">{allTimeTop.title}</div>
                <div className="stats-callout-sub">{allTimeTop.artist}</div>
                <div className="stats-callout-metric">{formatNumber(allTimeTop.wiki_view_count)} views of all time</div>
              </div>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
